import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { expenseApi } from '../../services/api';

const ExpenseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    interval: 'monthly',
    monthly_split: false
  });
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all expenses to extract categories
        const expensesResponse = await expenseApi.getAll();
        const uniqueCategories = [...new Set(expensesResponse.data.map(expense => expense.category))];
        setCategories(uniqueCategories);
        
        // If editing, fetch expense details
        if (isEditMode) {
          const expenseResponse = await expenseApi.getById(id);
          const expense = expenseResponse.data;
          
          setFormData({
            name: expense.name,
            category: expense.category,
            interval: expense.interval,
            monthly_split: expense.monthly_split
          });
        }
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === 'new') {
      // Show input for new category
      setFormData(prevData => ({
        ...prevData,
        category: ''
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        category: value
      }));
    }
  };

  const handleNewCategoryChange = (e) => {
    setNewCategory(e.target.value);
  };

  const handleNewCategorySubmit = () => {
    if (newCategory.trim()) {
      setCategories(prev => [...prev, newCategory.trim()]);
      setFormData(prevData => ({
        ...prevData,
        category: newCategory.trim()
      }));
      setNewCategory('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Ensure monthly_split is a boolean
      const processedData = {
        ...formData,
        monthly_split: Boolean(formData.monthly_split)
      };
      
      if (isEditMode) {
        await expenseApi.update(id, processedData);
      } else {
        await expenseApi.create(processedData);
      }
      navigate('/expenses');
    } catch (err) {
      setError('Failed to save expense');
      console.error(err);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h1 className="display-5 mb-3">{isEditMode ? 'Edit Expense' : 'Add New Expense'}</h1>
          
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </div>
      
      <div className="row">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="name" className="form-label fw-bold">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="category" className="form-label fw-bold">Category</label>
                  {categories.length > 0 ? (
                    <select
                      className="form-select"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleCategoryChange}
                      required
                    >
                      <option value="">Select a category</option>
                      {categories.map((category, index) => (
                        <option key={index} value={category}>{category}</option>
                      ))}
                      <option value="new">+ Add new category</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="Enter a category"
                      required
                    />
                  )}
                </div>
                
                {formData.category === '' && categories.length > 0 && (
                  <div className="mb-3">
                    <label htmlFor="newCategory" className="form-label">New Category</label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        id="newCategory"
                        value={newCategory}
                        onChange={handleNewCategoryChange}
                        placeholder="Enter new category"
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleNewCategorySubmit}
                      >
                        <i className="fas fa-plus me-1"></i> Add
                      </button>
                    </div>
                  </div>
                )}
                
                <hr className="my-4" />
                <h5 className="mb-3">Expense Settings</h5>
                
                <div className="mb-3">
                  <label htmlFor="interval" className="form-label">Recurrence Interval</label>
                  <select
                    className="form-select"
                    id="interval"
                    name="interval"
                    value={formData.interval}
                    onChange={handleChange}
                    required
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                    <option value="one-time">One-time</option>
                  </select>
                </div>
                
                <div className="mb-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="monthly_split"
                      name="monthly_split"
                      checked={formData.monthly_split}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="monthly_split">
                      Split cost monthly
                    </label>
                    <div className="form-text mt-1">
                      If checked, the cost will be distributed evenly across months for reporting purposes.
                    </div>
                  </div>
                </div>
                
                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-success px-4">
                    <i className="fas fa-save me-2"></i>
                    {isEditMode ? 'Update Expense' : 'Create Expense'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/expenses')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseForm;
